import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { leagueApi } from "@bw/shared/api";
import { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { FacilityScopePicker } from "../../../common/components/FacilityScopePicker";
import { queryKeys } from "../../../common/query/queryClient";
import { LeagueOfferingFacts } from "../components/LeagueOfferingFacts";

export function LeagueDirectoryPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const city = params.get("city") ?? "";
  const facilityId = params.get("facility_id") || undefined;
  const seasonId = params.get("season_id") || undefined;
  const rawPage = Number(params.get("page"));
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const [draftQuery, setDraftQuery] = useState(query);
  const [draftCity, setDraftCity] = useState(city);
  useEffect(() => {
    setDraftQuery(query);
    setDraftCity(city);
  }, [query, city]);
  const filters = {
    query: query || undefined,
    city: city || undefined,
    facility_id: facilityId,
    season_id: seasonId,
    limit: 20,
    offset: (page - 1) * 20,
  };
  const result = useQuery({
    queryKey: [...queryKeys.leagues.all, "public-discovery", filters],
    queryFn: () => leagueApi.discover(filters),
  });
  const update = (values: Record<string, string>) =>
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(values)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
  return (
    <Page title="Leagues">
      <Stack spacing={2.5}>
        <Stack
          component="form"
          role="search"
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          onSubmit={(event) => {
            event.preventDefault();
            update({ q: draftQuery.trim(), city: draftCity.trim(), page: "" });
          }}
        >
          <TextField
            label="League or division"
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            fullWidth
          />
          <TextField
            label="City"
            value={draftCity}
            onChange={(event) => setDraftCity(event.target.value)}
          />
          <Button type="submit" variant="contained">
            Search
          </Button>
        </Stack>
        <FacilityScopePicker
          facilityId={facilityId}
          onChange={(value) => update({ facility_id: value ?? "", page: "" })}
        />
        {seasonId ? (
          <Alert
            severity="info"
            action={
              <Button onClick={() => update({ season_id: "", page: "" })}>
                All current seasons
              </Button>
            }
          >
            Showing the linked season.
          </Alert>
        ) : null}
        {result.isLoading ? (
          <Typography role="status">Loading league divisions…</Typography>
        ) : result.isError ? (
          <Alert
            severity="error"
            action={
              <Button onClick={() => void result.refetch()}>Retry</Button>
            }
          >
            Could not load league divisions.
          </Alert>
        ) : (
          <>
            <Typography role="status" aria-live="polite" variant="body2">
              {result.data?.total ?? 0}{" "}
              {(result.data?.total ?? 0) === 1 ? "division" : "divisions"} found
            </Typography>
            {!result.data?.items.length ? (
              <Alert
                severity="info"
                action={
                  <Button
                    onClick={() =>
                      update({
                        q: "",
                        city: "",
                        facility_id: "",
                        season_id: "",
                        page: "",
                      })
                    }
                  >
                    Clear filters
                  </Button>
                }
              >
                No league divisions found. Try another city or facility.
              </Alert>
            ) : (
              result.data.items.map((offering) => (
                <Paper
                  component="article"
                  key={offering.division_id}
                  variant="outlined"
                  sx={{ p: 2.5 }}
                >
                  <Stack spacing={1.5}>
                    <Typography component="h2" variant="h6">
                      {offering.league_name}
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {offering.division_name}
                    </Typography>
                    <LeagueOfferingFacts offering={offering} />
                    <Button
                      component={RouterLink}
                      to={`${offering.detail_url}${facilityId ? `?${new URLSearchParams({ facility_id: facilityId })}` : ""}`}
                      variant="outlined"
                      sx={{ alignSelf: "flex-start" }}
                    >
                      View division
                    </Button>
                  </Stack>
                </Paper>
              ))
            )}
            {result.data && (page > 1 || result.data.has_more) ? (
              <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                <Button
                  disabled={page === 1}
                  onClick={() => update({ page: String(page - 1) })}
                >
                  Previous
                </Button>
                <Typography>Page {page}</Typography>
                <Button
                  disabled={!result.data.has_more}
                  onClick={() => update({ page: String(page + 1) })}
                >
                  Next
                </Button>
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
    </Page>
  );
}
